import { arrayMax, FieldViewModel, groupBy, numericTextSort, selectMany } from "../omnia"
import { IDataLayer, ISoilService } from "../types"
import { setupFieldAnalysisHistory } from "./history"
import { RowViewModel } from "./rowViewModel"

export class RowManager {

    constructor(private soilSvc: ISoilService) {

    }

    public groupRowsByField(source: RowViewModel[], cropping: boolean): RowViewModel[] {

        var rows = selectMany(source.map(r => r.groupByField()));

        rows = rows.filter(r => (r != null) && (!r.unallocated));

        var allocatedFields = rows.filter(r => !!r.field).map(r => r.field);

        rows = rows.concat(this.createRowsForMissingFields(allocatedFields, cropping));

        rows = rows.filter(r => r.field && (!r.field.disabled))

        var groups = groupBy(rows, r => r, r => r.targetId);

        groups = groups.sort((g1, g2) => {

            var d = (g1.key.unallocated ? 1 : 0) - (g2.key.unallocated ? 1 : 0);

            if ((!d) && (!g1.key.unallocated)) {
                d = g1.key.fieldDescription.localeCompare(g2.key.fieldDescription);
            }

            return d;

        });

        var alt = 0;

        groups.forEach(g => {

            var groupAlt = !!(alt++ % 2);

            g.values = setupFieldAnalysisHistory(g.values);

            g.values.forEach(r => {
                r.alternateRow = groupAlt;
            });

        });

        rows = selectMany(groups.map(g => g.values));

        rows = rows.sort((a, b) => {

            if (cropping) {
                var descriptionOrder = a.field.field.Name.localeCompare(b.field.field.Name);

                if (descriptionOrder !== 0) {
                    return descriptionOrder;
                }

                if (a.field.cropping.SowingDate < b.field.cropping.SowingDate) {
                    return 1;
                } else if (a.field.cropping.SowingDate > b.field.cropping.SowingDate) {
                    return -1;
                }
            } else {
                var descriptionOrder = a.fieldDescription.localeCompare(b.fieldDescription);

                if (descriptionOrder !== 0) {
                    return descriptionOrder;
                }
            }

            if (!a.pastAnalysis && b.pastAnalysis) {
                return -1;
            } else if (a.pastAnalysis && !b.pastAnalysis) {
                return 1;
            }

            if (a.analysis.SamplingDate > b.analysis.SamplingDate) {
                return -1;
            } else if (a.analysis.SamplingDate < b.analysis.SamplingDate) {
                return 1;
            }

            return 0;
        });

        return rows;

    }

    public groupRowsByZone(source: RowViewModel[], allRows: RowViewModel[]): ng.IPromise<RowViewModel[]> {

        return this.soilSvc.getSoilAnalysisLayers(true).then(layers => {

            var rows = selectMany(this.soilSvc.activeFields.map(f => {
                var analysisLayers = layers.filter(l => l.FieldId === f.fieldId);
                var analysisLayer: IDataLayer = null;
                if (analysisLayers.length === 0) {
                    return [];
                } else if (analysisLayers.length > 1) {
                    var maxId = arrayMax(analysisLayers, l => l.DataLayerId);
                    analysisLayer = analysisLayers.find(a => a.DataLayerId === maxId);
                } else {
                    analysisLayer = analysisLayers[0];
                }

                if (analysisLayer.Zones && analysisLayer.Zones.length > 0) {
                    var fieldRows = allRows.filter(r => r.allocations &&
                        r.allocations.filter(a => a.AllocToField &&
                            a.Field.fieldId === f.fieldId &&
                            a.Allocations.filter(aa => !(<any>aa).ZoneAlloc).length > 0).length > 0);

                    var maxDate = arrayMax(fieldRows, r => r.SamplingDate);
                    var fieldRow = fieldRows.find(r => r.SamplingDate === maxDate);
                    var allZonesAllocated = analysisLayer.Zones.every(z => !!z.Value);
                    if (fieldRow) {
                        fieldRow = new RowViewModel(fieldRow.analysis, this.soilSvc, fieldRow.allocations.filter(a => a.AllocToField && a.Field.fieldId === f.fieldId));
                    } else if (!fieldRow) {
                        fieldRow = RowViewModel.createModelForField(this.soilSvc, f);
                    }

                    if (allZonesAllocated) {
                        fieldRow.soilAnalysisLayerId = analysisLayer.DataLayerId;
                    }

                    var zoneRows = analysisLayer.Zones.sort((a, b) => a.Number - b.Number).map(z => {
                        var zRows = allRows.filter(r => r.allocations &&
                            r.allocations.filter(a =>
                                a.ZonesAllocations.filter(aa =>
                                    aa.ZoneId === z.DataLayerZoneId).length > 0).length > 0);

                        var maxDate = arrayMax(zRows, r => r.SamplingDate);
                        var zoneRow = zRows.find(r => r.SamplingDate === maxDate);
                        if (!zoneRow) {
                            zoneRow = RowViewModel.createModelForZone(this.soilSvc, f, z);
                        } else {
                            zoneRow = new RowViewModel(zoneRow.analysis, this.soilSvc,
                                zoneRow.allocations.filter(r =>
                                    r.ZonesAllocations.filter(a => a.ZoneId === z.DataLayerZoneId).length > 0));
                            zoneRow.zoneNumber = z.Number;
                        }

                        return zoneRow;
                    });

                    return [fieldRow].concat(zoneRows);
                }

                return [];
            }));

            // TODO : Sort rows if needed
            return rows;
        });
    }

    private createRowsForMissingFields(allocated: FieldViewModel[], cropping: boolean): RowViewModel[] {

        let source = cropping ? this.soilSvc.cropping.filter(c => c.currentYear) : this.soilSvc.activeFields;

        let missing = source.filter(i => allocated.indexOf(i) < 0);

        return missing.map(i => RowViewModel.createModelForField(this.soilSvc, i));

    }

}


export function sortAnalysisRows(rows: RowViewModel[], filter: boolean) {

    if (filter) {
        rows = rows.filter(r => !!r.soilAnalysis);
    }

    rows = rows.sort((a, b) => {

        var diff = (b.SamplingDate ? b.SamplingDate.valueOf() : 0) - (a.SamplingDate ? a.SamplingDate.valueOf() : 0);
        //if (!diff && a.CreatedOn && b.CreatedOn) diff = b.CreatedOn.valueOf() - a.CreatedOn.valueOf();
        if (!diff) {
            if (a.AnalysisNumber && !b.AnalysisNumber) return 1;
            if (!a.AnalysisNumber && b.AnalysisNumber) return -1;
            if (!a.AnalysisNumber && !b.AnalysisNumber) return 0;

            var res = numericTextSort([a, b], x => x.AnalysisNumber);
            diff = res.indexOf(a) === 0 ? -1 : 1;
        }

        return diff;

    });

    rows.forEach((r, index) => r.alternateRow = !!(index % 2))

    return rows

}
